import { ref, watch } from "vue";

import { useAppStore } from "@/stores";

const TTL_MS = 5 * 60 * 1000;
const KEY_PREFIX = "cache:alm-events";

const events = ref([]);
const lastFetched = ref(null);
const isLoading = ref(false);
const error = ref(null);

let activeKey = null;
let inflight = null;
let watcherStarted = false;

function buildKey(userId, company) {
  return `${KEY_PREFIX}:${company || "anon"}:${userId || "anon"}`;
}

function hydrate(key) {
  if (activeKey === key) return;
  activeKey = key;
  events.value = [];
  lastFetched.value = null;
  const raw = localStorage.getItem(key);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw);
    events.value = Array.isArray(parsed.value) ? parsed.value : [];
    lastFetched.value = typeof parsed.timestamp === "number" ? parsed.timestamp : null;
  } catch {
    // Corrupt cache entry — drop it; next fetch will repopulate.
    localStorage.removeItem(key);
  }
}

function persist() {
  if (!activeKey) return;
  localStorage.setItem(
    activeKey,
    JSON.stringify({
      value: events.value,
      timestamp: lastFetched.value,
    }),
  );
}

function startUserWatcher() {
  if (watcherStarted) return;
  watcherStarted = true;
  const store = useAppStore();
  watch(
    () => [store.user?.id, store.user?.company],
    ([id, company]) => {
      hydrate(buildKey(id, company));
    },
    { immediate: true },
  );
}

export function useAlmEvents() {
  startUserWatcher();
  return { events, lastFetched, isLoading, error };
}

function isStale() {
  return !lastFetched.value || Date.now() - lastFetched.value >= TTL_MS;
}

export function refreshAlmEvents(fetcher, { force = false } = {}) {
  if (!force) {
    if (inflight) return inflight;
    if (!isStale()) return Promise.resolve();
  }
  const previous = inflight;
  isLoading.value = true;
  error.value = null;
  const request = (async () => {
    if (previous) await Promise.allSettled([previous]);
    try {
      const result = await fetcher();
      events.value = result;
      lastFetched.value = Date.now();
      persist();
    } catch (e) {
      error.value = e;
      throw e;
    } finally {
      if (inflight === request) {
        isLoading.value = false;
        inflight = null;
      }
    }
  })();
  inflight = request;
  return request;
}
